import { Dropdown, FilterContext, Input, Modal } from '@/components';
import { Table } from '@/components/Table';
import { ColumnDef } from '@/components/Table/types';
import { RequestKpi } from '@/modules/Requests/domain';
import {
  useGetRequestsClaimants,
  useGetRequestsKpisDetailsInfinite,
} from '@/modules/Requests/queries';
import {
  KpiFilterType,
  KpiOrderByType,
  SortDirection,
  StatusFilter,
  TypologyValue,
} from '@/shared';
import { KpisDetailsRowsPerPage } from '@/shared/constants';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { CloseIcon } from '@/components/Icons';

interface KpiDetailsModalProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  columns: ColumnDef<RequestKpi>[];
  orderByValues: KpiOrderByType[];
  filterByValues: KpiFilterType[];
}

export const KpiDetailsModal = ({
  isOpen,
  onClose,
  title,
  columns,
  filterByValues,
  orderByValues,
}: KpiDetailsModalProps) => {
  const { t } = useTranslation();

  const typologyOptions = useMemo(
    () =>
      (
        Object.values(TypologyValue).filter(
          (value) => typeof value === 'number',
        ) as number[]
      ).map((value: number) => ({
        value,
        label: t(`chart_${TypologyValue[value]}_type`),
      })),
    [t],
  );

  const statusOptions = useMemo(
    () =>
      (
        Object.values(StatusFilter).filter(
          (value) => typeof value === 'number',
        ) as number[]
      ).map((value: number) => ({
        value: value,
        label: t(`status_filter.${StatusFilter[value]}`),
      })),
    [t],
  );

  const [filterBy, setFilterBy] = useState<KpiFilterType>();
  const [sortBy, setSortBy] = useState<string | undefined>(
    KpiOrderByType[KpiOrderByType.CreationDateDesc],
  );
  const [currentTypologyFilter, setCurrentTypologyFilter] =
    useState<TypologyValue>();
  const [currentClaimant, setCurrentClaimant] = useState<number>();
  const [currentStatus, setCurrentStatus] = useState<number>();
  const [reference, setReference] = useState<string>();

  const [sortOrder, setSortOrder] = useState<SortDirection>('descending');

  const { showActiveOnly } = useContext(FilterContext);

  const { data: requestClaimants } = useGetRequestsClaimants();

  const filter = useMemo(() => {
    switch (filterBy) {
      case KpiFilterType.Typology:
        return currentTypologyFilter;
      case KpiFilterType.Claimant:
        return currentClaimant;
      case KpiFilterType.Status:
        return currentStatus;
      case KpiFilterType.Reference:
        return reference;
      default:
        return undefined;
    }
  }, [
    currentClaimant,
    currentStatus,
    currentTypologyFilter,
    filterBy,
    reference,
  ]);

  const { data, isLoading, fetchNextPage, isFetching } =
    useGetRequestsKpisDetailsInfinite({
      onlyActiveRequests: showActiveOnly,
      filter,
      filterBy,
      sortBy,
      sortOrder,
    });

  const resetFilter = useCallback(() => {
    setCurrentTypologyFilter(undefined);
    setCurrentClaimant(undefined);
    setCurrentStatus(undefined);
    setReference(undefined);
  }, []);

  const onResetFilterBy = useCallback(() => {
    resetFilter();
    setFilterBy(undefined);
  }, [resetFilter]);

  const onChangeFilterBy = useCallback(
    (newFilterBy?: KpiFilterType) => {
      resetFilter();
      setFilterBy(newFilterBy);
    },
    [resetFilter],
  );

  // Change the Sort Order based on Sort By field
  useEffect(() => {
    const descendingOrderTypes = [
      KpiOrderByType[KpiOrderByType.CreationDateDesc],
      KpiOrderByType[KpiOrderByType.EstimatedHours],
      KpiOrderByType[KpiOrderByType.UsedHours],
    ];

    setSortOrder(
      descendingOrderTypes.includes(sortBy ?? '') ? 'descending' : 'ascending',
    );
  }, [sortBy]);

  const kpiFilterTypeOptions = useMemo(
    () =>
      filterByValues.map((value) => ({
        value: value,
        label: t(`kpi_filter_type_${KpiFilterType[value]}`),
      })),
    [filterByValues, t],
  );

  const kpiOrderByTypeOptions = useMemo(
    () =>
      orderByValues.map((value) => ({
        value: KpiOrderByType[value],
        label: t(`kpi_orderBy_type_${KpiOrderByType[value]}`),
      })),
    [orderByValues, t],
  );

  const requestsKpis = useMemo(() => data?.pages.flat() || [], [data?.pages]);

  const { ref: lastRowRef } = useInView({
    onChange: (inView) => {
      if (inView && !isFetching) {
        fetchNextPage?.();
      }
    },
  });

  const options = useMemo(() => {
    switch (filterBy) {
      case KpiFilterType.Typology:
        return {
          options: typologyOptions,
          value: currentTypologyFilter,
          setValue: setCurrentTypologyFilter,
          onRemoveAllSelectedClick: () => setCurrentTypologyFilter(undefined),
        };
      case KpiFilterType.Claimant:
        return {
          options:
            requestClaimants?.map((claimant) => ({
              value: claimant.id,
              label: claimant.fullName,
            })) ?? [],
          value: currentClaimant,
          setValue: setCurrentClaimant,
          onRemoveAllSelectedClick: () => setCurrentClaimant(undefined),
        };
      case KpiFilterType.Status:
        return {
          options: statusOptions,
          value: currentStatus,
          setValue: setCurrentStatus,
          onRemoveAllSelectedClick: () => setCurrentStatus(undefined),
        };
    }

    return {
      options: [],
      value: undefined,
      setValue: () => {},
      onRemoveAllSelectedClick: () => {},
    };
  }, [
    currentClaimant,
    currentStatus,
    currentTypologyFilter,
    filterBy,
    requestClaimants,
    statusOptions,
    typologyOptions,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      handleClose={onClose}
      allowClickOutsideToClose
      hideCloseButton
      modalClassName="h-[610px]"
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between gap-2.5">
          <p className="text-md font-bold text-gray">{title}</p>
          <CloseIcon onClick={onClose} className="cursor-pointer" />
        </div>
        <div className="flex justify-end gap-10 text-sm font-black text-gray">
          <div className="flex flex-row items-center gap-2.5">
            <span>{t('filter')}</span>
            <Dropdown
              className="w-36"
              placeholder={t('select_filter')}
              options={kpiFilterTypeOptions}
              selectedValue={filterBy}
              onSelect={onChangeFilterBy}
              onRemoveAllSelectedClick={onResetFilterBy}
            />
            {!filterBy ||
            [
              KpiFilterType.Typology,
              KpiFilterType.Claimant,
              KpiFilterType.Status,
            ].includes(filterBy) ? (
              <Dropdown
                className="w-36"
                placeholder={t('filter_by')}
                options={options.options}
                selectedValue={options.value}
                onSelect={options.setValue}
                onRemoveAllSelectedClick={options.onRemoveAllSelectedClick}
                disabled={!options.options.length}
              />
            ) : (
              <Input
                className="w-36"
                value={reference}
                name="filter"
                onChange={(e) => setReference(e.target.value)}
                onResetInput={() => setReference(undefined)}
              />
            )}
          </div>
          <div className="flex items-center gap-2.5">
            <span>{t('order')}</span>
            <Dropdown
              className="w-40"
              placeholder={t('select_orderBy')}
              options={kpiOrderByTypeOptions}
              selectedValue={sortBy}
              onSelect={setSortBy}
              onRemoveAllSelectedClick={() => setSortBy(undefined)}
            />
          </div>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          <Table
            columns={columns}
            rows={requestsKpis ?? []}
            emptyStateMessage={t('requests_details_empty_data')}
            isLoading={isLoading}
            loadingRows={KpisDetailsRowsPerPage}
            isFetching={isFetching}
            lastRowRef={lastRowRef}
          />
        </div>
      </div>
    </Modal>
  );
};
